import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Input,
  Text,
  VStack,
  useToast,
  Image,
  Progress,
  Flex,
  Icon,
  Badge,
  Container,
  Heading,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Radio,
  RadioGroup,
  Stack,
} from "@chakra-ui/react";
import { AttachmentIcon, CheckIcon, WarningIcon } from "@chakra-ui/icons";
import { initializePaddle } from "@paddle/paddle-js";
import { useLocation, useNavigate } from "react-router-dom";
import axiosClient from "../../../axios-client";
import { useStateContext } from "../../../context/ContextProvider";
import { useTranslation } from "react-i18next";
import { use } from "react";

const GestPaiement = () => {

  const { user, setUser, setToken, dataSubscriptionUser,getAbonnementClient } = useStateContext();
  const { t } = useTranslation();
  const navigation = useNavigate()
  const location = useLocation();

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  
  const [paymentMethod, setPaymentMethod] = useState("manual");
  const [paddle, setPaddle] = useState(null);
  const toast = useToast();

  React.useEffect(() => {
    initializePaddle({ 
      environment: process.env.REACT_APP_PADDLE_ENVIRONMENT || 'sandbox', 
      token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN,
      eventCallback: function(data) {
        if (data.name === "checkout.completed") {
          // Fermer la modale de paiement Paddle
          if (window.Paddle) {
            window.Paddle.Checkout.close();
          }

          toast({
            title: t("Succès"),
            description: t("Paiement validé. Activation de l'abonnement en cours..."),
            status: "success",
            duration: 5000,
            isClosable: true,
          });

          // Attendre 3 secondes pour laisser le temps au webhook côté serveur de s'exécuter
          setTimeout(() => {
            getAbonnementClient();
            navigation('/PlansAbonnement');
          }, 3000);
        }
      }
    }).then(
      (paddleInstance) => {
        if (paddleInstance) {
          setPaddle(paddleInstance);
        }
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const validateFile = (file) => {
    if (!file) return false;

    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: t("Fichier trop volumineux"),
        description: t("La taille maximale autorisée est de 5MB"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    if (
      file.type !== "application/pdf" &&
      !file.type.startsWith("image/")
    ) {
      toast({
        title: t("Format non supporté"),
        description: t("Seuls les fichiers PDF et images sont acceptés"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    processFile(selectedFile);
  };

  const processFile = (selectedFile) => {
    if (selectedFile && validateFile(selectedFile)) {
      setFile(selectedFile);
      if (selectedFile.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = () => setPreview(reader.result);
        reader.readAsDataURL(selectedFile);
      } else {
        setPreview(null);
      }
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFile = e.dataTransfer.files[0];
    processFile(droppedFile);
  }, []);

  const simulateUpload = async () => {

    setLoading(true);
    setUploadProgress(0);

    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 200));
      setUploadProgress(i);
    }


    const formData = new FormData();
    // Ajouter le fichier au FormData
    formData.append('payment_proof', file);


    await axiosClient.post(`subscribe-to-plan/${location?.state?.planId}/?client=${user?.id}`, formData).then(async (res) => {
      if (res.data.status === "error") {
        // console.log(res.data);
        
        setLoading(false);
        toast({
          title: res.data.message,
          description: res.data.error,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      else if (res.data.error === 'Client is disabled.' && res.data.etat === false) {
        setUser({});
        setToken(null);
        localStorage.removeItem("tokenExpirationTime");
        localStorage.removeItem("refreshTimeAdmin");
        localStorage.removeItem("role");

        // Vérifier si le toast n'est pas déjà affiché
        if (!toast.isActive('accountDisabled')) {
          toast({
            id: 'accountDisabled',
            description: t("le compte est désactivé"),
            status: 'warning',
            variant: 'left-accent',
            duration: 2000,
            isClosable: true,
            position: 'bottom-right'
          });
        }
        return;
      }
      else if (res.data.error === "Client not found") {
        setUser({});
        setToken(null);
        localStorage.removeItem("tokenExpirationTime");
        localStorage.removeItem("refreshTimeAdmin");
        localStorage.removeItem("role");

        // Vérifier si le toast n'est pas déjà affiché
        if (!toast.isActive('accountDisabled')) {
          toast({
            id: 'accountDisabled',
            description: t("Client non trouvé"),
            status: 'warning',
            variant: 'left-accent',
            duration: 2000,
            isClosable: true,
            position: 'bottom-right'
          });
        }
        return;
      }
      else if (res.data.error === "Plan is disabled.") {
        setLoading(false);
        toast({
          description: res.data.message,
          status: 'warning',
          variant: 'left-accent',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right'
        });
        return;
      }
      else if (res.data.error === "Plan limit reached.") {
        setLoading(false);
        toast({
          description: res.data.message,
          status: 'warning',
          variant: 'left-accent',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right'
        });
        return;
      }
      else if (res.data.error === "Plan not found") {
        setLoading(false);
        toast({
          description: res.data.message,
          status: 'warning',
          variant: 'left-accent',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right'
        });
        return;
      }
      else {
        setLoading(false);
        getAbonnementClient()
        navigation('/PlansAbonnement')
        toast({
          title: t("Envoi réussi"),
          description: t("Votre preuve de paiement a été envoyée avec succès"),
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    }).catch((error) => {
      setLoading(false);
      toast({
        title: t("Erreur"),
        description: t("Une erreur s'est produite lors de l'envoi du fichier"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    })
  };

  const handleSubmit = async () => {
    if (!file) {
      toast({
        title: t("Erreur"),
        description: t("Veuillez sélectionner un fichier"),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    await simulateUpload();
  };

  const bgBoxDragActive = useColorModeValue("gray.50", "gray.800")
  const bgBox = useColorModeValue("white", "gray.900")

  const borderColorBoxDragActive = useColorModeValue("blue.500", "blue.800")
  const borderColorBox = useColorModeValue("gray.300", "gray.700")

  const bgBoxFile = useColorModeValue("gray.50", "gray.800")

  return (
    <Container maxW="container.md" py={8}>
      <VStack spacing={6} align="stretch">
        <Heading size="lg" textAlign="center">
          {t("Paiement Abonnement")} {location.state.planName}
        </Heading>

        <RadioGroup onChange={setPaymentMethod} value={paymentMethod}>
          <Stack direction={{ base: 'column', md: 'row' }} spacing={5} justify="center" p={4} borderWidth={1} borderRadius="md" bg={bgBoxFile}>
            <Radio value="manual" colorScheme="blue">{t("Virement Bancaire (Manuel)")}</Radio>
            <Radio value="paddle" colorScheme="green">{t("Paiement en Ligne Sécurisé")}</Radio>
          </Stack>
        </RadioGroup>

        {paymentMethod === "manual" ? (
            <>
              <Box
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                borderWidth={2}
                borderStyle="dashed"
                borderRadius="md"
                p={6}
          textAlign="center"
          bg={dragActive ? bgBoxDragActive : bgBox}
          borderColor={dragActive ? borderColorBoxDragActive : borderColorBox}
          transition="all 0.2s"
          position={"relative"}
        >
          <FormControl>
            <FormLabel>
              <VStack spacing={4}>
                <Icon as={AttachmentIcon} w={8} h={8} color="gray.400" />
                <Text>
                  {t("Glissez-déposez votre fichier ici ou")}{" "}
                  <Button size="sm" colorScheme="blue" variant="link">
                    {t("parcourez vos fichiers")}
                  </Button>
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {t("Formats acceptés : PDF, JPG, PNG (max 5MB)")}
                </Text>
              </VStack>
            </FormLabel>
          </FormControl>
          <Input
            position={"absolute"}
            top={0}
            left={0}
            height={"100%"}
            id="file-upload"
            type="file"
            accept=".pdf,image/*"
            onChange={handleFileChange}
            opacity={0}
          // display="none"
          />
        </Box>

        {file && (
          <Box
            borderWidth={1}
            borderRadius="md"
            p={4}
            bg={bgBoxFile}
          >
            <Flex align="center" justify="space-between">
              <Flex align="center" gap={3}>
                <Icon
                  as={file.type === "application/pdf" ? AttachmentIcon : null}
                  w={5}
                  h={5}
                />
                <Text fontWeight="medium" isTruncated>
                  {file.name}
                </Text>
              </Flex>
              <Badge colorScheme="green">
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </Badge>
            </Flex>
          </Box>
        )}

        {preview && (
          <Box borderWidth={1} borderRadius="md" overflow="hidden">
            <Image src={preview} alt={t("Aperçu du fichier")} maxH="300px" objectFit="contain" />
          </Box>
        )}

        {loading && (
          <Progress
            value={uploadProgress}
            size="sm"
            colorScheme="blue"
            borderRadius="full"
          />
        )}

        <Button
          colorScheme="blue"
          size="lg"
          onClick={handleSubmit}
          isLoading={loading}
          loadingText={t("Envoi en cours...")}
          leftIcon={<Icon as={CheckIcon} />}
        >
          {t("Envoyer la preuve de paiement")}
        </Button>
            </>
          ) : (
            <Button
              colorScheme="green"
              size="lg"
              onClick={() => {
                paddle?.Checkout.open({
                  items: [{ priceId: process.env.REACT_APP_PADDLE_PRICE_ID, quantity: 1 }],
                  customData: { client_id: user?.id ? String(user.id) : "", plan_id: location.state?.planId ? String(location.state.planId) : "" }
                });
              }}
              leftIcon={<Icon as={CheckIcon} />}
            >
              {t("Payer maintenant en ligne")}
            </Button>
          )}
      </VStack>
    </Container>
  );
};

export default GestPaiement;